import { useEffect, useRef, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import {
  AuthProvider,
  hasCognitoGroup,
  useAuth,
} from "./features/auth/AuthContext";
import ProtectedRoute from "./features/auth/ProtectedRoute";
import {
  ConfirmPage,
  ForgotPage,
  RegisterPage,
  ResetPage,
  SignInPage,
} from "./features/auth/AuthPages";
import { API_BASE_URL, API_PATH } from "./constants/api";
import { authenticatedApiRequest } from "./features/api/apiClient";
import {
  Circle,
  CircleMarker,
  MapContainer,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";

type DirectoryEntry = {
  providerId: string;
  active: boolean;
  licenseNumber: string;
  facilityName: string;
  speciality: string;
  locationCounty: string;
  formattedAddress: string;
  telephoneNumber: string;
  facilityPOC: string;
  licensedBedCount: string;
  hasReports: string;
};
type DirectoryPage = {
  items: DirectoryEntry[];
  pageSize: number;
  totalCount?: number;
  hasMore?: boolean;
  nextToken?: string;
};
type DirectoryMapEntry = Pick<
  DirectoryEntry,
  | "providerId"
  | "facilityName"
  | "speciality"
  | "locationCounty"
  | "formattedAddress"
  | "telephoneNumber"
  | "facilityPOC"
  | "licensedBedCount"
> & { latitude: number; longitude: number; distanceInMiles: number };
type DirectoryMapPage = {
  items: DirectoryMapEntry[];
  totalCount: number;
  zipCode: string;
  radiusInMiles: number;
  center: { latitude: number; longitude: number; formattedAddress?: string };
};
declare global {
  interface Window {
    google?: any;
  }
}
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const getPostalCode = (result: any) =>
  String(
    (result?.address_components || []).find((component: any) =>
      component.types?.includes("postal_code"),
    )?.long_name || "",
  );
const statusBody = (row: DirectoryEntry) => (
  <Tag
    value={row.active ? "Active" : "Inactive"}
    severity={row.active ? "success" : "secondary"}
  />
);
const headerButtonStyle = { color: "#ffffff" };

function WorkspaceHeader({
  onSignOut,
  onBack,
}: {
  onSignOut: () => void;
  onBack?: () => void;
}) {
  return (
    <header className="workspace-topbar">
      <div className="workspace-brand">
        <img src="/Teal%20CBP%20bridge%20emblem.png" alt="" />
        <span>Care Bridge Partners</span>
      </div>
      <div className="workspace-topbar-actions">
        {onBack && (
          <Button
            label="Back to workspace"
            icon="pi pi-arrow-left"
            text
            style={headerButtonStyle}
            onClick={onBack}
          />
        )}
        <Button
          label="Sign out"
          icon="pi pi-sign-out"
          text
          style={headerButtonStyle}
          onClick={onSignOut}
        />
      </div>
    </header>
  );
}

function Workspace() {
  const { session, signOut } = useAuth();
  const nav = useNavigate();
  return (
    <main className="workspace-page">
      <WorkspaceHeader
        onSignOut={() => {
          signOut();
          nav("/signin");
        }}
      />
      <section className="workspace-shell workspace-home-actions">
        {hasCognitoGroup(session, "businessAdmin") && (
          <Button
            label="View WA Directory"
            icon="pi pi-table"
            onClick={() => nav("/wa-directory")}
          />
        )}
      </section>
    </main>
  );
}

function WaDirectoryPage() {
  const { session, signOut } = useAuth();
  const nav = useNavigate();
  const [page, setPage] = useState<DirectoryPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [previousTokens, setPreviousTokens] = useState<string[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [exporting, setExporting] = useState(false);
  useEffect(() => {
    if (!API_BASE_URL) {
      setMessage("Configure the API URL for this environment.");
      setLoading(false);
      return;
    }
    const query = new URLSearchParams({ limit: "100" });
    if (nextToken) query.set("nextToken", nextToken);
    if (debouncedSearch) query.set("search", debouncedSearch);
    setLoading(true);
    setMessage(null);
    void authenticatedApiRequest(
      session?.getIdToken().getJwtToken() || "",
      `${API_PATH.PROVIDERS}?${query.toString()}`,
    )
      .then(async (response) => {
        if (response.status === 401) {
          signOut();
          nav("/signin");
          return;
        }
        if (!response.ok)
          throw new Error("Unable to load the Washington provider directory.");
        const result = (await response.json()) as DirectoryPage;
        if (!Array.isArray(result.items))
          throw new Error("The provider directory response is invalid.");
        setPage(result);
      })
      .catch((error) =>
        setMessage(
          error instanceof Error
            ? error.message
            : "Unable to load the Washington provider directory.",
        ),
      )
      .finally(() => setLoading(false));
  }, [nextToken, debouncedSearch, session, signOut, nav]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setNextToken(undefined);
      setPreviousTokens([]);
      setPageNumber(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);
  const entries = page?.items || [];
  const changePage = (direction: "next" | "previous") => {
    if (direction === "next" && page?.nextToken) {
      setPreviousTokens((tokens) => [...tokens, nextToken || ""]);
      setNextToken(page.nextToken);
      setPageNumber((current) => current + 1);
    }
    if (direction === "previous" && previousTokens.length) {
      const priorToken = previousTokens[previousTokens.length - 1];
      setPreviousTokens((tokens) => tokens.slice(0, -1));
      setNextToken(priorToken || undefined);
      setPageNumber((current) => current - 1);
    }
  };
  const exportDirectory = async () => {
    try {
      setExporting(true);
      setMessage(null);
      const response = await authenticatedApiRequest(
        session?.getIdToken().getJwtToken() || "",
        `${API_PATH.PROVIDERS}?export=true`,
        {
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        },
      );
      if (response.status === 401) {
        signOut();
        nav("/signin");
        return;
      }
      if (!response.ok)
        throw new Error("Unable to export the Washington provider directory.");
      if (
        !response.headers
          .get("content-type")
          ?.includes(
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          )
      )
        throw new Error(
          "The Excel export service is not available in this environment yet.",
        );
      const downloadUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "washington-afh-directory.xlsx";
      link.click();
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Unable to export the Washington provider directory.",
      );
    } finally {
      setExporting(false);
    }
  };
  return (
    <main className="workspace-page">
      <WorkspaceHeader
        onBack={() => nav("/home")}
        onSignOut={() => {
          signOut();
          nav("/signin");
        }}
      />
      <section className="p-3 md:p-5">
        <header className="mb-4">
          <h1 className="mt-0 mb-1">Washington AFH Directory</h1>
          <p className="text-600 m-0">
            Provider information from the Washington AFH directory.
          </p>
        </header>
        {message && (
          <Message severity="error" text={message} className="w-full mb-3" />
        )}
        <Card>
          <div className="directory-toolbar">
            <div className="directory-search">
              <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search providers"
                />
              </span>
            </div>
            <div className="directory-toolbar-actions">
              <Button
                label="Map View"
                icon="pi pi-map"
                outlined
                onClick={() => nav("/wa-directory/map")}
              />
              <Button
                label="Export Excel"
                icon="pi pi-file-excel"
                outlined
                loading={exporting}
                disabled={loading}
                onClick={() => void exportDirectory()}
              />
              <Tag
                value={`${page?.totalCount ?? entries.length} total records`}
                severity="info"
              />
            </div>
          </div>
          <div className="directory-table">
            <DataTable
              value={entries}
              loading={loading}
              stripedRows
              removableSort
              filterDisplay="menu"
              emptyMessage="No providers match your search or filter."
              tableStyle={{ minWidth: "65rem" }}
            >
              <Column
                field="facilityName"
                header="Facility"
                sortable
                filter
                style={{ minWidth: "16rem" }}
              />
              <Column field="licenseNumber" header="License" sortable filter />
              <Column field="speciality" header="Specialty" sortable filter />
              <Column field="locationCounty" header="County" sortable filter />
              <Column
                field="formattedAddress"
                header="Address"
                sortable
                filter
                style={{ minWidth: "18rem" }}
              />
              <Column field="telephoneNumber" header="Phone" filter />
              <Column
                field="facilityPOC"
                header="Point of contact"
                sortable
                filter
              />
              <Column field="licensedBedCount" header="Beds" sortable filter />
            </DataTable>
          </div>
          <div className="directory-cards">
            {loading ? (
              <span>Loading providers…</span>
            ) : (
              entries.map((entry) => (
                <article
                  className="directory-provider-card"
                  key={entry.providerId}
                >
                  <div className="directory-card-title">
                    <strong>{entry.facilityName}</strong>
                    {statusBody(entry)}
                  </div>
                  <dl>
                    <div>
                      <dt>License</dt>
                      <dd>{entry.licenseNumber}</dd>
                    </div>
                    <div>
                      <dt>Specialty</dt>
                      <dd>{entry.speciality || "—"}</dd>
                    </div>
                    <div>
                      <dt>County</dt>
                      <dd>{entry.locationCounty || "—"}</dd>
                    </div>
                    <div>
                      <dt>Address</dt>
                      <dd>{entry.formattedAddress || "—"}</dd>
                    </div>
                    <div>
                      <dt>Phone</dt>
                      <dd>{entry.telephoneNumber || "—"}</dd>
                    </div>
                    <div>
                      <dt>Point of contact</dt>
                      <dd>{entry.facilityPOC || "—"}</dd>
                    </div>
                    <div>
                      <dt>Beds</dt>
                      <dd>{entry.licensedBedCount || "—"}</dd>
                    </div>
                  </dl>
                </article>
              ))
            )}
          </div>
          <div className="directory-pagination">
            <span>
              Page {pageNumber} · {entries.length} records returned
            </span>
            <div>
              <Button
                label="Previous"
                icon="pi pi-chevron-left"
                outlined
                disabled={loading || previousTokens.length === 0}
                onClick={() => changePage("previous")}
              />
              <Button
                label="Next"
                icon="pi pi-chevron-right"
                iconPos="right"
                disabled={loading || !page?.hasMore || !page.nextToken}
                onClick={() => changePage("next")}
              />
            </div>
          </div>
        </Card>
      </section>
    </main>
  );
}

function MapFocus({ entry }: { entry: DirectoryMapEntry | null }) {
  const map = useMap();
  useEffect(() => {
    if (entry)
      map.flyTo(
        [entry.latitude, entry.longitude],
        Math.max(map.getZoom(), 13),
        { duration: 0.5 },
      );
  }, [entry, map]);
  return null;
}

function WaDirectoryMapPage() {
  const { session, signOut } = useAuth();
  const nav = useNavigate();
  const [zipCode, setZipCode] = useState("");
  const [radiusInMiles, setRadiusInMiles] = useState(5);
  const [result, setResult] = useState<DirectoryMapPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [focusedEntry, setFocusedEntry] = useState<DirectoryMapEntry | null>(
    null,
  );
  const [googleReady, setGoogleReady] = useState(
    Boolean(window.google?.maps?.places),
  );
  const addressInputRef = useRef<HTMLInputElement>(null);
  const searchMap = async (requestedZipCode = zipCode.trim()) => {
    if (!/^\d{5}$/.test(requestedZipCode)) {
      setMessage("Enter a valid five-digit ZIP code.");
      return;
    }
    try {
      setLoading(true);
      setMessage(null);
      const query = new URLSearchParams({
        mapSearch: "true",
        zipCode: requestedZipCode,
        radiusInMiles: String(radiusInMiles),
      });
      const response = await authenticatedApiRequest(
        session?.getIdToken().getJwtToken() || "",
        `${API_PATH.PROVIDERS}?${query.toString()}`,
      );
      if (response.status === 401) {
        signOut();
        nav("/signin");
        return;
      }
      const body = await response.json();
      if (!response.ok)
        throw new Error(body.message || "Unable to search the provider map.");
      setResult(body as DirectoryMapPage);
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Unable to search the provider map.",
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY || !addressInputRef.current) return;
    const initializeGooglePlaces = () => {
      const autocomplete = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        {
          types: ["geocode"],
          componentRestrictions: { country: "us" },
          fields: ["address_components", "formatted_address"],
        },
      );
      autocomplete.addListener("place_changed", () => {
        const zip = getPostalCode(autocomplete.getPlace());
        if (!zip) {
          setMessage("Choose an address with a ZIP code.");
          return;
        }
        setZipCode(zip);
        void searchMap(zip);
      });
    };
    if (window.google?.maps?.places) {
      initializeGooglePlaces();
      setGoogleReady(true);
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => {
      initializeGooglePlaces();
      setGoogleReady(true);
    };
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, []);
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY || !googleReady || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      new window.google.maps.Geocoder().geocode(
        { location: { lat: coords.latitude, lng: coords.longitude } },
        (results: any[]) => {
          const zip = getPostalCode(results?.[0]);
          if (zip) {
            setZipCode(zip);
            void searchMap(zip);
          }
        },
      );
    });
  }, [googleReady]);
  return (
    <main className="workspace-page">
      <WorkspaceHeader
        onBack={() => nav("/wa-directory")}
        onSignOut={() => {
          signOut();
          nav("/signin");
        }}
      />
      <section className="p-3 md:p-5">
        <header className="mb-4">
          <h1 className="mt-0 mb-1">Washington AFH Map</h1>
          <p className="text-600 m-0">
            Search providers around a Washington ZIP code.
          </p>
        </header>
        {message && (
          <Message severity="error" text={message} className="w-full mb-3" />
        )}
        <Card>
          <div className="directory-map-search">
            <InputText
              ref={addressInputRef}
              placeholder="Search an address"
              className="directory-address-picker"
            />
            <InputText
              value={zipCode}
              onChange={(event) =>
                setZipCode(event.target.value.replace(/\D/g, "").slice(0, 5))
              }
              placeholder="ZIP code"
              inputMode="numeric"
            />
            <Dropdown
              value={radiusInMiles}
              options={Array.from({ length: 20 }, (_, index) => ({
                label: `${(index + 1) * 5} miles`,
                value: (index + 1) * 5,
              }))}
              onChange={(event) => setRadiusInMiles(event.value)}
            />
            <Button
              label="Search"
              icon="pi pi-search"
              loading={loading}
              onClick={() => void searchMap()}
            />
          </div>
          {result && (
            <>
              <div className="directory-map-summary">
                <Tag
                  value={`${result.totalCount} providers within ${result.radiusInMiles} miles`}
                  severity="info"
                />
                <span>{result.center.formattedAddress || result.zipCode}</span>
              </div>
              <div className="directory-map-layout">
                <aside
                  className="directory-map-results"
                  aria-label="Provider search results"
                >
                  {result.items.map((entry) => (
                    <Button
                      key={entry.providerId}
                      text
                      className={`directory-map-result${focusedEntry?.providerId === entry.providerId ? " is-focused" : ""}`}
                      onClick={() => setFocusedEntry(entry)}
                    >
                      <strong>{entry.facilityName}</strong>
                      <span>{entry.formattedAddress}</span>
                      <small>{entry.distanceInMiles} miles away</small>
                    </Button>
                  ))}
                </aside>
                <MapContainer
                  key={`${result.center.latitude}-${result.center.longitude}-${result.radiusInMiles}`}
                  center={[result.center.latitude, result.center.longitude]}
                  zoom={10}
                  className="directory-map"
                >
                  <TileLayer
                    attribution="&copy; OpenStreetMap contributors"
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapFocus entry={focusedEntry} />
                  {focusedEntry && (
                    <Popup
                      key={focusedEntry.providerId}
                      position={[focusedEntry.latitude, focusedEntry.longitude]}
                      autoPan
                      className="directory-location-popup"
                    >
                      <strong>{focusedEntry.facilityName}</strong>
                      <span>{focusedEntry.formattedAddress}</span>
                      <small>
                        <b>Specialty:</b> {focusedEntry.speciality || "—"}
                      </small>
                      <small>
                        <b>County:</b> {focusedEntry.locationCounty || "—"}
                      </small>
                      <small>
                        <b>Phone:</b> {focusedEntry.telephoneNumber || "—"}
                      </small>
                      <small>
                        <b>Point of contact:</b>{" "}
                        {focusedEntry.facilityPOC || "—"}
                      </small>
                      <small>
                        <b>Licensed beds:</b>{" "}
                        {focusedEntry.licensedBedCount || "—"}
                      </small>
                      <small>
                        <b>Distance:</b> {focusedEntry.distanceInMiles} miles
                      </small>
                    </Popup>
                  )}
                  <Circle
                    center={[result.center.latitude, result.center.longitude]}
                    radius={result.radiusInMiles * 1609.344}
                    pathOptions={{ color: "#4a2f23" }}
                  />
                  <CircleMarker
                    center={[result.center.latitude, result.center.longitude]}
                    radius={8}
                    pathOptions={{
                      color: "#a96343",
                      fillColor: "#a96343",
                      fillOpacity: 1,
                    }}
                  />
                  {result.items.map((entry) => (
                    <CircleMarker
                      key={entry.providerId}
                      center={[entry.latitude, entry.longitude]}
                      radius={6}
                      pathOptions={{
                        color:
                          focusedEntry?.providerId === entry.providerId
                            ? "#a96343"
                            : "#54765d",
                        fillColor:
                          focusedEntry?.providerId === entry.providerId
                            ? "#a96343"
                            : "#54765d",
                        fillOpacity: 0.9,
                      }}
                      eventHandlers={{
                        click: () => {
                          setFocusedEntry(entry);
                        },
                      }}
                    />
                  ))}
                </MapContainer>
              </div>
            </>
          )}{" "}
          {!result && !loading && (
            <p className="text-600 mb-0">
              Enter a ZIP code and select a radius to see nearby providers.
            </p>
          )}
        </Card>
      </section>
    </main>
  );
}

function BusinessAdminDirectoryRoute() {
  const { session } = useAuth();
  return hasCognitoGroup(session, "businessAdmin") ? (
    <WaDirectoryPage />
  ) : (
    <Navigate to="/home" replace />
  );
}
function BusinessAdminDirectoryMapRoute() {
  const { session } = useAuth();
  return hasCognitoGroup(session, "businessAdmin") ? (
    <WaDirectoryMapPage />
  ) : (
    <Navigate to="/home" replace />
  );
}
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/confirm" element={<ConfirmPage />} />
          <Route path="/forgot-password" element={<ForgotPage />} />
          <Route path="/reset-password" element={<ResetPage />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/home" element={<Workspace />} />
            <Route path="/app" element={<Navigate to="/home" replace />} />
            <Route
              path="/wa-directory"
              element={<BusinessAdminDirectoryRoute />}
            />
            <Route
              path="/wa-directory/map"
              element={<BusinessAdminDirectoryMapRoute />}
            />
          </Route>
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
export default App;
